import React from 'react';
import { colors, M } from '@dashboard-experience/mastodon';
import { Field, formValueSelector } from 'redux-form';
import { useSelector } from 'react-redux';
import { i18n } from '@international/mastodon-i18n';
import styled from 'styled-components';
import { generateUSStatesOptions } from 'components/fields/helper';
import { LicenseOnFile } from 'api/verifications/types';
import Select from '../../fields/mastodon/Select';
import { TextInput } from '../../fields/mastodon';
import * as V from '../../../lib/validations';
import { VERIFICATION_FORM_NAME } from '../../../constants';

const DriverLicenseInfoCard = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${colors.uiGrey200};
  border-radius: 12px;
`;

const StateField = styled.div`
  flex: 0 0 auto;
  width: 196px;
`;

const NumberField = styled.div`
  flex: 1 1 200px;
`;

// Own row below the inputs. Carbon caps notification width and adds its own
// margins, which read as a stray inset inside the card.
const NoticeRow = styled.div`
  flex: 1 1 100%;

  .cds--inline-notification {
    max-width: 100%;
    margin: 0;
  }
`;

export interface DriverLicenseInfoProps {
  fieldKey: string;
  licensesOnFile?: LicenseOnFile[];
}

const SELECT_STATE_OPTION = { value: '', name: 'Select state' };

const DriverLicenseInfo = ({
  fieldKey,
  licensesOnFile = [],
}: DriverLicenseInfoProps) => {
  const dlNumberKey = `${fieldKey}.dl_number`;
  const dlStateKey = `${fieldKey}.dl_state`;
  const stateOptions = [SELECT_STATE_OPTION, ...generateUSStatesOptions()];

  // Already-searched states stay selectable so the candidate can find their
  // state and be told why it won't work, rather than hunting for a missing
  // option. Submission is blocked by the form-level validate.
  const selectedState = useSelector((state: any) =>
    formValueSelector(VERIFICATION_FORM_NAME)(state, dlStateKey),
  );
  const alreadySearched = licensesOnFile.some(
    license => license.state === selectedState,
  );

  return (
    <DriverLicenseInfoCard>
      <StateField>
        <Field
          name={dlStateKey}
          label={i18n.getStr(
            'components.Report.Verification.MatrixVerification.DrivingExperience.form.dlState.label',
          )}
          component={Select}
          context={dlStateKey}
          options={stateOptions}
          dataTestId='verification-dl-state'
          validate={[V.required]}
        />
      </StateField>
      <NumberField>
        <Field
          type='text'
          id='verification-dl-number'
          testId='verification-dl-number'
          name={dlNumberKey}
          component={TextInput}
          context={dlNumberKey}
          placeholder='D1234567'
          labelControl={i18n.getStr(
            'components.Report.Verification.MatrixVerification.DrivingExperience.form.dlNumber.label',
          )}
          // Required would paint "Required." on a field the candidate cannot
          // type into. Submit is already blocked by the form-level state check.
          validate={alreadySearched ? undefined : [V.required]}
          disabled={alreadySearched}
        />
      </NumberField>
      {alreadySearched && (
        <NoticeRow>
          <M.InlineNotification
            kind='warning'
            title=''
            subtitle={i18n.getStr(
              'components.Report.Verification.MatrixVerification.DrivingExperience.form.alreadySearched',
              { state: selectedState },
            )}
            data-testid='dee-state-already-searched'
            hideCloseButton
          />
        </NoticeRow>
      )}
    </DriverLicenseInfoCard>
  );
};

export default i18n.renderTranslation()(DriverLicenseInfo);
